import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import type { ReactNode, FormEventHandler } from 'react';

interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  loading?: boolean;
  /**
   * When true the submit button is disabled even if `loading` is false.
   * Use this to gate Save while a background dependency (a job, a
   * required field) isn't ready — Cancel stays enabled.
   */
  submitDisabled?: boolean;
  /**
   * Optional content rendered on the right side of the title row.
   * Use this for mode toggles, helper links, etc. that belong in the
   * header rather than the body.
   */
  titleActions?: ReactNode;
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Generic dialog used for create/edit forms. Renders the body in a
 * vertical stack so each module's form just lists fields top-to-bottom.
 */
export function FormDialog({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel = 'Save',
  loading,
  submitDisabled,
  titleActions,
  children,
  maxWidth = 'sm',
}: FormDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <form onSubmit={onSubmit} noValidate>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flexGrow: 1 }}>{title}</Box>
            {titleActions}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading || submitDisabled}>{submitLabel}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
